import { of as observableOf, Observable, Subject } from 'rxjs';

import { switchMap, debounceTime } from 'rxjs/operators';
import { MapsLocation } from './../../../core/maps-search/maps-location.model';
import { Component, OnInit, OnDestroy, NgZone, Inject } from '@angular/core';
import { ProjectService } from '../../services/project.service';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { ProjectFile } from 'app/interfaces/project-file.model';
import { DatepickerOptions } from 'ng2-datepicker';
import * as moment from 'moment';
import { Detail } from 'app/interfaces/detail.model';
import { Project } from 'app/interfaces/project.model';
import { TagModel } from 'ngx-chips/core/accessor';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-project-form',
  templateUrl: './project-form.component.html',
  styleUrls: ['./project-form.component.scss'],
  providers: [ProjectService],
})
export class ProjectFormComponent implements OnInit, OnDestroy {
  activeTab = 0;
  fields: any = {};
  result: any = {};
  saving = false;
  phaseLookup = ['None', 'Competition', 'Design', 'In progress', 'On site', 'Realized'];
  scaleOptions = [];
  sustainabilityCertificateOptions = [];
  programOptions = [];
  detailOptions = [];
  typologyOptions = [];
  materialOptions = [];
  conceptOptions = [];
  structureOptions = [];
  themeOptions = [];
  employees = [];
  selectedDetail: Detail = null;
  fileBlueprint: any = {
    Phase: 0,
    Category: '',
    UsedInWebsite: false,
    UsedInArchive: false,
    UsedInPressKit: false,
  };
  years = this.projectService.getYears(60, 15);
  currentYear = this.projectService.getCurrentYear();
  groupCollapsed = false;
  project: Project;
  // creditGroups = [];
  selectedCreditGroup: number;
  allGroupsCollapsed = false;

  // Debounce subjects for date updates
  private startDateSubject = new Subject<number>();
  private endDateSubject = new Subject<number>();

  // @TODO: implement intial location
  location: MapsLocation = {
    latitude: 51.9202494,
    longitude: 4.4873258999999734,
    name: 'Markthal, Verlengde Nieuwstraat, 3011 Rotterdam, Netherlands',
  };

  updateLocation(location: MapsLocation) {
    // @TODO: implement save location
    if (location) {
      this.project.Latitude = location.latitude;
      this.project.Longitude = location.longitude;
      this.project.Address = location.name;
    } else {
      // Remove location when no location is set
      this.project.Latitude = null;
      this.project.Longitude = null;
      this.project.Address = null;
    }
  }

  get selectedScales() {
    return this.scaleOptions.filter(opt => opt.checked).map(opt => opt.value);
  }

  get selectedSustainabilityCertficates() {
    return this.sustainabilityCertificateOptions.filter(opt => opt.checked).map(opt => opt.value);
  }

  get selectedTypologies() {
    return this.typologyOptions.filter(opt => opt.checked).map(opt => opt.value);
  }

  get selectedPrograms() {
    return this.programOptions.filter(opt => opt.checked).map(opt => opt.value);
  }

  get selectedThemes() {
    return this.themeOptions.filter(opt => opt.checked).map(opt => opt.value);
  }

  quillConfigLite = {
    toolbar: [['bold', 'italic', 'underline', 'strike'], ['clean']],
  };

  selectedDetails: Array<any> = [];
  selectedStructures: Array<any> = [];
  selectedMaterials: Array<any> = [];
  selectedConcepts: Array<any> = [];

  // public datePickerOptions = new DatepickerOptions({
  //   autoApply: true,
  //   format: 'MM/d/YYYY'
  // });

  constructor(
    private projectService: ProjectService,
    private route: ActivatedRoute,
    private _ngZone: NgZone,
    private router: Router,
    @Inject(DOCUMENT) private document: any
  ) {}

  ngOnInit() {
    this.activeTab = 0;

    // Set up debounced date observers
    this.startDateSubject.pipe(
      debounceTime(2000) // Wait 2s after the last emission
    ).subscribe(year => {
      const date = new Date(+year, 0, 1);
      this.project.StartDate = date;
    });

    this.endDateSubject.pipe(
      debounceTime(2000) // Wait 2s after the last emission
    ).subscribe(year => {
      if (year == 0 || year === null) {
        this.project.EndDate = null;
        return;
      }

      const date = new Date(+year, 11, 31);
      this.project.EndDate = date;
    });

    this.route.params
      .pipe(
        // (+) converts string 'id' to a number
        switchMap((params: Params) => {
          return this.projectService.single(params['id'] || 0);
        })
      )
      .subscribe((item: any) => {
        this.project = item;

        this.projectService.fields().subscribe(a => {
          this.fields = a;

          this.fillOptions(this.fields.scales, this.scaleOptions, 'Scales');
          this.fillOptions(this.fields.sustainabilityCertificates, this.sustainabilityCertificateOptions, 'SustainabilityCertificates');
          this.fillOptions(this.fields.programs, this.programOptions, 'Programs');
          this.fillOptions(this.fields.themes, this.themeOptions, 'Themes');
          this.fillOptions(this.fields.details, this.detailOptions, 'Details');
          this.fillOptions(this.fields.typologies, this.typologyOptions, 'Typologies');
          this.fillOptions(this.fields.structures, this.structureOptions, 'Structures');
          this.fillOptions(this.fields.materials, this.materialOptions, 'Materials');
          this.fillOptions(this.fields.concepts, this.conceptOptions, 'Concepts');
          this.employees = this.fields.employees;
        });

        this.location = {
          latitude: this.project.Latitude,
          longitude: this.project.Longitude,
          name: this.project.Address,
        };

        this.selectedStructures = this.project.Structures.map(a => {
          return { id: a.Id, text: a.Name };
        });
        this.selectedDetails = this.project.Details.map(a => {
          return { id: a.Id, text: a.Title };
        });
        this.selectedMaterials = this.project.Materials.map(a => {
          return { id: a.Id, text: a.Name };
        });
        this.selectedConcepts = this.project.Concepts.map(a => {
          return { id: a.Id, text: a.Name };
        });

        this.project.Teams.forEach(a => {
          a.selectedMembers = a.Members.map(b => {
            return { id: b.Id, text: b.Name };
          });
          // const d = moment(a.PhaseStart);
          // a.PhaseStart = new DateModel({
          //   day: `${d.date()}`,
          //   month: `${d.month() + 1}`,
          //   year: `${d.year()}`,
          //   formatted: `${d.month() + 1}/${d.date()}/${d.year()}`,
          //   momentObj: d
          // });
          // const d2 = moment(a.PhaseEnd);
          // a.PhaseEnd = new DateModel({
          //   day: `${d2.date()}`,
          //   month: `${d2.month() + 1}`,
          //   year: `${d2.year()}`,
          //   formatted: `${d2.month() + 1}/${d2.date()}/${d2.year()}`,
          //   momentObj: d2
          // });

          a.Subgroups.forEach(b => {
            b.selectedMembers = b.Members.map(c => {
              return { id: c.Id, text: c.Name };
            });
          });
        });
      });
  }

  toSelectFields(collection) {
    if (!collection) {
      return [];
    }
    return collection.map(a => {
      return { id: a.Id, text: a.Name || a.Title };
    });
  }

  fillOptions(source, dest, field) {
    if (!source || !this.project) {
      return;
    }
    // tslint:disable-next-line:forin
    for (const x in source) {
      if (field === 'Themes') {
        dest.push({
          value: source[x],
          checked: this.project[field].some(e => e.id === source[x].id),
        });
      } else {
        dest.push({
          value: source[x],
          checked: this.project[field].some(e => e.Id === source[x].Id),
        });
      }
    }
  }

  formatDetailData(obj) {
    return this.fields.available_details;
  }

  updateGroup(event, phase, subIndex: number = -1) {
    const selectedEmployees = this.employees.filter(a => {
      return (
        event
          .map(b => {
            return b.id;
          })
          .indexOf(a.Id) >= 0
      );
    });

    if (subIndex < 0) {
      this.project.Teams[phase].Members = selectedEmployees;
      this.project.Teams[phase].selectedMembers = event;
    } else {
      this.project.Teams[phase].Subgroups[subIndex].Members = selectedEmployees;
      this.project.Teams[phase].Subgroups[subIndex].selectedMembers = event;
    }
  }

  /*
   * Update ngSelect for materials, details, concepts and structures
   */
  updateNgSelect(event, index) {
    if (index === 'Details') {
      this.project[index] = this.fields.details.filter(
        a =>
          event
            .map(b => {
              return b.id;
            })
            .indexOf(a.Id) >= 0
      );
    } else {
      this.project[index] = event.map(d => {
        return { Id: d.id, Name: d.text };
      });
    }
  }

  setStartDate(year: number) {
    // Emit the year to the debounced subject instead of directly updating
    this.startDateSubject.next(year);
  }

  setEndDate(year: number) {
    // Emit the year to the debounced subject instead of directly updating
    this.endDateSubject.next(year);
  }

  onNgSelectTyped(event) {}

  addGroup(phase, index: number = -1) {
    const defaultMembers = [
      {
        Country: null,
        Id: 1,
        ImageUrl: 'http://www.mvrdv.nl/media//scraped/9689826d-c5b6-4f55-8ede-7292be2d1b9e.jpg',
        Name: 'Winy Maas',
        Name_EN: 'Winy Maas',
      },
      {
        Country: null,
        Id: 2,
        ImageUrl: 'http://www.mvrdv.nl/media//scraped/6c6e5bf4-4f9a-4860-82de-cfaf083fc138.jpg',
        Name: 'Jacob van Rijs',
        Name_EN: 'Jacob van Rijs',
      },
      {
        Country: null,
        Id: 3,
        ImageUrl: 'http://www.mvrdv.nl/media//scraped/a5510a0c-b478-404d-91e1-ac530eb23f4b.jpg',
        Name: 'Nathalie de Vries',
        Name_EN: 'Nathalie de Vries',
      },
    ];

    if (index < 0) {
      this.project.Teams.push({
        Order: this.project.Teams.length,
        Phase: phase,
        Members: defaultMembers,
        selectedMembers: defaultMembers.map(b => {
          return { id: b.Id, text: b.Name };
        }),
      });
    } else {
      if (!this.project.Teams[index].Subgroups) {
        this.project.Teams[index].Subgroups = [];
      }
      this.project.Teams[index].Subgroups.push({
        Phase: phase,
        Members: [],
        selectedMembers: [],
      });
    }
  }

  removeGroup(index, subIndex?: number) {
    if (subIndex === undefined) {
      this.project.Teams.splice(index, 1);
    } else {
      this.project.Teams[index].Subgroups.splice(subIndex, 1);
    }
  }

  saveProject() {
    this.saving = true;
    this.project.Scales = this.selectedScales;
    this.project.SustainabilityCertificates = this.selectedSustainabilityCertficates;
    this.project.Programs = this.selectedPrograms;
    this.project.Themes = this.selectedThemes;
    this.project.Typologies = this.selectedTypologies;
    // this.project.MainImage = this.project.mainImage;

    this.project.Teams.forEach(a => {
      if (a.PhaseStart) {
        a.PhaseStart = a.PhaseStart.momentObj;
      }

      if (a.PhaseEnd) {
        a.PhaseEnd = a.PhaseEnd.momentObj;
      }

      a.MembersToSave = a.Members;
      if (a.Subgroups) {
        a.Subgroups.forEach(b => {
          b.MembersToSave = b.Members;
        });
      }
    });

    try {
      const mainImageFile = this.project.Files.filter(a => a.UsedAsWebsiteMainImage)[0];
      this.project.MainImage = mainImageFile;

      this.project.Themes.forEach(t => {
        (<any>t).proofLinks = null;
      });
    } catch (e) {
      console.log(e);
    }

    this.project.Files.forEach((f, i) => {
      f.order = i;
    });

    this.project.TeamsToSave = this.project.Teams;

    this.projectService.save(this.project).subscribe(a => {
      const that = this;
      this.result = a.Result;
      this.project.Id = a.Project.Id;
      //      this.saving = false;

      // Needed TimeOut for save button animation.
      setTimeout(function () {
        that.saving = false;
      }, 900);
    });
  }

  toggleCalendar(e) {
    e.target.toggleCalendar();
  }

  addDetail() {
    this.selectedDetail = {
      Categories: [],
      Description: null,
      CreatedOn: null,
      Files: [],
      FileUrl: null,
      MainImage: null,
      Id: 0,
      Title: null,
      Materials: [],
      Number: null,
      RevisedOn: null,
      Structures: [],
      Team: [],
    };
  }

  editDetail(e: MouseEvent, detail: Detail) {
    this.selectedDetail = detail;
  }

  saveDetail(e: Detail) {
    if (this.selectedDetail.Id === 0) {
      // New
      this.project.Details = [...this.project.Details, e];
    } else {
      // Existing
      const detailIndex = this.project.Details.indexOf(this.selectedDetail);
      this.project.Details[detailIndex] = e;
    }
    this.selectedDetail = null;
  }
  onMemberAdded(tag: TagModel): Observable<any> {
    if (typeof tag === 'string') {
      tag = { Id: 0, Name: tag.toString().trim(), Name_EN: tag.toString().trim(), NAME_CN: null };
    }
    return observableOf(tag);
  }

  compareFn(val1, val2): boolean {
    return val1 && val2 ? val1.Id === val2.Id : val1 === val2;
  }

  compareYears(val1, val2): boolean {
    return val1 === val2;
  }

  log(a: any) {
    console.log(a);
  }

  collapseAll() {
    this.project.Teams.forEach(team => {
      team.groupCollapsed = !this.allGroupsCollapsed;
    });

    this.allGroupsCollapsed = !this.allGroupsCollapsed;
  }

  delete() {
    if (confirm('Are you sure you want to delete this project? This cannot be undone.')) {
      this.projectService.delete(this.project).subscribe(x => {
        if (x) {
          this.router.navigate(['/']);
        }
      });
    }
  }

  // dragStart(e, i) {
  //   this.selectedCreditGroup = i;
  //   console.log(this.selectedCreditGroup);
  // }

  // dragOver(e, i) {
  //   this._ngZone.runOutsideAngular(() => {
  //     if (e.target.classList.contains('group')) {
  //       if (i < this.project.Teams.length - 1) {
  //         this.project.Teams.filter(a => a.Order > i).map(b => b.Order + 1);
  //         return this.project.Teams[this.selectedCreditGroup].Order = i - 1;
  //       } else {
  //         this.project.Teams[this.project.Teams.length - 1].Order = this.project.Teams.length - 2;
  //         return this.project.Teams[this.selectedCreditGroup].Order = this.project.Teams.length - 1;
  //       }
  //     }
  //   });
  // }

  ngOnDestroy() {
    // Complete the subjects to prevent memory leaks
    this.startDateSubject.complete();
    this.endDateSubject.complete();
  }
}
